import {
  AGENCY_LINK_RESUME_QUERY,
  extractAdAccountsFromIntegration,
  filterAccountsByAvailablePlatforms,
  isAgencyLinkingPlatformAvailable,
  markAgencyLinkingPickerShown,
  withAgencyLinkResumeBatchID,
} from "../utils/agencyAccountLinking.js";

const PENDING_KEY = "agencyAccountLinking:pendingLink";
const OAUTH_QUERY_KEYS = ["code", "auth_code", "state", "error", "scope"];

let linkingEnabled = false;
let availabilityReady = false;
let consentBlocked = false;
let showPickerLine = false;
let availablePlatforms = [];
let pickerHandler = null;
let resumeHandler = null;
let queuedResume = false;

export const PICKER_NOT_SHOWN = Object.freeze({
  shown: false,
  closed: Promise.resolve(),
});

// The OAuth popup loads the app too; it must not resume links or open the
// picker, since it shares localStorage with the opener and is closed early.
const isInsidePopup = () =>
  typeof window !== "undefined" &&
  Boolean(window.opener) &&
  window.opener !== window;

const readStorage = (key) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota and private-mode failures */
  }
};

export const setAgencyAccountLinkingEnabled = (enabled) => {
  linkingEnabled = enabled === true;
};

export const isAgencyAccountLinkingEnabledNow = () => linkingEnabled;

export const setAgencyLinkingAvailability = ({
  availablePlatforms: platforms = [],
  consentBlocked: blocked = false,
  showPickerLine: pickerLine = false,
  ready = true,
} = {}) => {
  availablePlatforms = Array.isArray(platforms) ? platforms : [];
  consentBlocked = blocked === true;
  showPickerLine = pickerLine === true;
  availabilityReady = ready === true;
};

export const isAgencyLinkingAvailabilityReady = () => availabilityReady;

export const getAgencyLinkingAvailablePlatforms = () => availablePlatforms;

export const savePendingAgencyLink = (pending) => {
  writeStorage(PENDING_KEY, pending);
};

export const peekPendingAgencyLink = () => {
  const pending = readStorage(PENDING_KEY);
  if (!pending) return null;
  if (pending.expiresAt && pending.expiresAt < Date.now()) {
    writeStorage(PENDING_KEY, null);
    return null;
  }
  return pending;
};

export const consumePendingAgencyLink = () => {
  const pending = peekPendingAgencyLink();
  writeStorage(PENDING_KEY, null);
  return pending;
};

export const clearPendingAgencyLink = () => writeStorage(PENDING_KEY, null);

const emitResume = () => {
  if (!peekPendingAgencyLink()) return;
  if (resumeHandler) {
    resumeHandler();
    return;
  }
  queuedResume = true;
};

export const subscribeAgencyLinkingPicker = (handler) => {
  pickerHandler = handler;
  return () => {
    if (pickerHandler === handler) pickerHandler = null;
  };
};

export const subscribeAgencyLinkingResume = (handler) => {
  resumeHandler = handler;
  if (queuedResume && peekPendingAgencyLink()) {
    queuedResume = false;
    handler();
  }
  return () => {
    if (resumeHandler === handler) resumeHandler = null;
  };
};

/**
 * Returns `{ shown, closed }`. `closed` resolves once the picker is dismissed
 * (immediately when it was not shown) so callers can continue their own
 * post-connection flow afterwards.
 *
 * The picker only exists to carry the pre-checked consent line, so it opens
 * only when the backend sends showPickerLine (agency variant). Direct
 * advertisers are linked in the background and never see it.
 */
export const requestNewConnectionPicker = ({
  platform,
  integration,
  accounts: accountsOverride,
} = {}) => {
  if (
    !linkingEnabled ||
    consentBlocked ||
    !showPickerLine ||
    !pickerHandler ||
    isInsidePopup()
  ) {
    return PICKER_NOT_SHOWN;
  }
  if (peekPendingAgencyLink()) return PICKER_NOT_SHOWN;
  if (!isAgencyLinkingPlatformAvailable(platform, availablePlatforms)) {
    return PICKER_NOT_SHOWN;
  }

  const accounts =
    accountsOverride ??
    filterAccountsByAvailablePlatforms({
      accounts: extractAdAccountsFromIntegration({ platform, integration }),
      availablePlatforms,
    });
  if (accounts.length === 0) return PICKER_NOT_SHOWN;

  const integrationID = accounts[0].integrationID || accounts[0].id;
  if (!markAgencyLinkingPickerShown({ integrationID })) {
    return PICKER_NOT_SHOWN;
  }

  const handler = pickerHandler;
  const closed = new Promise((resolve) => {
    handler({
      platform: accounts[0].platform,
      integrationID,
      accounts,
      onClosed: resolve,
    });
  });
  return { shown: true, closed };
};

const currentSearch = () =>
  typeof window === "undefined" ? "" : window.location.search || "";

// `agencyLinkResume` stays the OAuth return query. Its value is the batch id.
const captureAgencyLinkResume = () => {
  const pending = peekPendingAgencyLink();
  if (!pending) return null;
  const next = withAgencyLinkResumeBatchID({
    pending,
    search: currentSearch(),
  });
  if (next !== pending) savePendingAgencyLink(next);
  return next;
};

const stripAgencyLinkResumeQuery = () => {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(AGENCY_LINK_RESUME_QUERY)) return;
    url.searchParams.delete(AGENCY_LINK_RESUME_QUERY);
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", next);
  } catch {
    /* ignore malformed locations */
  }
};

export const requestResumePendingLink = () => {
  if (!linkingEnabled || isInsidePopup() || !peekPendingAgencyLink()) {
    return false;
  }
  captureAgencyLinkResume();
  stripAgencyLinkResumeQuery();
  emitResume();
  return true;
};

export const isAgencyLinkingOAuthCallbackUrl = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (!params.get("state")) return false;
  return OAUTH_QUERY_KEYS.some((key) => key !== "state" && params.get(key));
};
